import { useAuthStore } from '@/stores/authStore';

/** Skills that must also run in the Chrome extension (live browser tabs). */
const BROWSER_TAB_SKILLS: Record<string, string> = {
  close_duplicates: 'closeDuplicates',
  auto_organize: 'autoOrganize',
  group_by_domain: 'groupByDomain',
  dead_tab_cleanup: 'closeDeadTabs',
};

type KhiimoriWindow = Window & {
  __khiimoriDispatch?: (action: string, payload: Record<string, unknown>) => void;
};

/** Pass web-app JWT to the extension so it can poll queued browser commands. */
function syncExtensionAuth(): void {
  const { tokens, user } = useAuthStore.getState();
  const token = tokens?.token || localStorage.getItem('authToken');
  const userId = user?.id;
  if (!token || userId == null || userId === '') return;
  dispatchExtensionAction(
    'login',
    { token, userId: String(userId) },
    { syncAuth: false },
  );
}

function dispatchExtensionAction(
  action: string,
  payload: Record<string, unknown> = {},
  opts: { syncAuth?: boolean } = {},
): void {
  if (opts.syncAuth !== false && action !== 'login') {
    syncExtensionAuth();
  }
  const message = {
    source: 'khiimori-app',
    action,
    payload,
  };

  const win = window as KhiimoriWindow;

  if (typeof win.__khiimoriDispatch === 'function') {
    try {
      win.__khiimoriDispatch(action, payload);
    } catch {
      // Bridge not installed yet.
    }
  }

  try {
    document.dispatchEvent(
      new CustomEvent('khiimori-extension-command', { detail: message, bubbles: true }),
    );
  } catch {
    // ignore
  }

  window.postMessage(message, '*');

  type ChromeRuntime = { sendMessage?: (id: string, msg: unknown, cb?: () => void) => void };
  const chromeApi = (window as unknown as { chrome?: { runtime?: ChromeRuntime } }).chrome;
  const extId = import.meta.env.VITE_KHIIMORI_EXTENSION_ID as string | undefined;
  if (extId && chromeApi?.runtime?.sendMessage) {
    try {
      chromeApi.runtime.sendMessage(extId, { action, payload });
    } catch {
      // Extension not installed or page not allowed.
    }
  }
}

/**
 * Ask the Khiimori Chrome extension to run a tab action on the real browser.
 */
export function notifyExtensionTabAction(
  skillId: string,
  payload: Record<string, unknown> = {},
): void {
  const action = BROWSER_TAB_SKILLS[skillId];
  if (!action) return;
  dispatchExtensionAction(action, { skillId, ...payload });
}

/** Pull pending tab commands queued by the API (close duplicates, etc.). */
export function requestExtensionCommandPoll(): void {
  dispatchExtensionAction('pollCommands', {});
}

/** Close live browser tabs by Chrome tab id and/or URL (used when deleting from Tab Monitor). */
export function closeBrowserTabs(opts: {
  chromeTabIds?: Array<string | number>;
  urls?: string[];
  /** When true, close tabs matching each URL (see singleUrlMatch). */
  forceCloseUrls?: boolean;
  /** When true with forceCloseUrls, close only one browser tab per URL. */
  singleUrlMatch?: boolean;
}): void {
  dispatchExtensionAction('closeTabs', {
    chrome_tab_ids: opts.chromeTabIds ?? [],
    urls: opts.urls ?? [],
    force_close_urls: Boolean(opts.forceCloseUrls),
    single_url_match: Boolean(opts.singleUrlMatch),
  });
  requestExtensionCommandPoll();
}

/** Open URLs in the live browser (used when restoring from Recycle). */
export function openBrowserTabs(urls: string[], active = true): void {
  const clean = urls.map((u) => u.trim()).filter(Boolean);
  if (clean.length === 0) return;
  dispatchExtensionAction('openTabs', { urls: clean, active });
  requestExtensionCommandPoll();
}

/** Enable browser-level distraction blocking during focus (requires extension). */
export function enableFocusBlocking(domains: string[], durationMinutes?: number): void {
  dispatchExtensionAction('enableFocusBlocking', {
    domains,
    blockedSites: domains,
    durationMinutes: durationMinutes ?? 0,
  });
  requestExtensionCommandPoll();
}

/** Disable browser-level distraction blocking. */
export function disableFocusBlocking(): void {
  dispatchExtensionAction('disableFocusBlocking', {});
  requestExtensionCommandPoll();
}

/** Pin or unpin a live browser tab (requires extension). */
export function pinBrowserTab(opts: { tabId?: number | string; url?: string; pinned?: boolean } = {}): void {
  dispatchExtensionAction('pinTab', {
    chrome_tab_id: opts.tabId,
    url: opts.url,
    pinned: opts.pinned !== false,
  });
  requestExtensionCommandPoll();
}

/** Mute or unmute audio on live browser tabs (requires extension). */
export function muteBrowserTabs(opts: { tabId?: number | string; url?: string; muted?: boolean } = {}): void {
  dispatchExtensionAction('muteTabs', {
    chrome_tab_id: opts.tabId,
    url: opts.url,
    muted: opts.muted !== false,
  });
  requestExtensionCommandPoll();
}

/** Duplicate an open browser tab (requires extension). */
export function duplicateBrowserTab(opts: { tabId?: number | string; url?: string } = {}): void {
  dispatchExtensionAction('duplicateTab', {
    chrome_tab_id: opts.tabId,
    url: opts.url,
  });
  requestExtensionCommandPoll();
}

/** Reload an open browser tab (requires extension). */
export function reloadBrowserTab(opts: { tabId?: number | string; url?: string; bypassCache?: boolean } = {}): void {
  dispatchExtensionAction('reloadTab', {
    chrome_tab_id: opts.tabId,
    url: opts.url,
    bypassCache: Boolean(opts.bypassCache),
  });
  requestExtensionCommandPoll();
}

/** Switch to/focus an open browser tab (requires extension). */
export function switchBrowserTab(opts: { tabId?: number | string; url?: string } = {}): void {
  dispatchExtensionAction('switchTab', {
    chrome_tab_id: opts.tabId,
    url: opts.url,
  });
  requestExtensionCommandPoll();
}

/** Save a bookmark to the browser (requires extension). */
export function createBrowserBookmark(opts: { url?: string; title?: string } = {}): void {
  dispatchExtensionAction('createBookmark', {
    url: opts.url,
    title: opts.title,
  });
  requestExtensionCommandPoll();
}

/** Extract text content from the active or specified browser tab (requires extension). */
export function extractBrowserContent(opts: { tabId?: number | string } = {}): void {
  dispatchExtensionAction('extractContent', {
    chrome_tab_id: opts.tabId,
  });
  requestExtensionCommandPoll();
}
